import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Domain-depth article (mapped to the "Educational" goal token) —
 * "One platform, eight foundry archetypes: why generality beats a point tool".
 * Surveys how grey iron, ductile, steel, aluminium, copper and zinc/Mg differ
 * in process and defects, then argues for a configurable twin over a hard-coded
 * tool. Vendor-neutral; product only in the closing block.
 */
export const FOUNDRY_ARCHETYPES_ARTICLE: Article = {
  slug: "eight-foundry-archetypes-one-platform",
  title: "One platform, eight foundry archetypes: why generality beats a point tool",
  deck:
    "Grey iron and pressure-die aluminium are both casting, but almost nothing else about them is the same — different metals, different moulds, different defects, different physics. A tool hard-coded for one will fight you on the next. The case for a twin you configure instead of one you replace.",
  goal: "Educational",
  readingMins: 9,
  heroStat: { value: "8", label: "foundry archetypes one model can describe" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "Walk through a grey-iron jobbing shop and then a high-pressure aluminium die-casting plant and you would be forgiven for thinking they belong to different industries. One pours a forgiving melt into sand at fourteen-hundred-odd degrees and lets gravity work; the other injects a light alloy into a steel die under enormous pressure in a fraction of a second. They share a word — casting — and little else. The metals differ, the moulds differ, the cycle times differ by orders of magnitude, and crucially, the things that go wrong differ. This is the trap waiting for anyone buying foundry software: a tool built around one archetype quietly assumes that archetype, and the assumptions leak.",
    },
    {
      kind: "paragraph",
      text:
        "It is worth being concrete about how varied the family is. Below are eight common archetypes. The point is not encyclopaedic completeness — it is to show that 'a foundry' is not one thing, and the differences are not cosmetic. They reach down into which process variables matter and which defects you spend your life chasing.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The family",
      heading: "Eight archetypes, eight different problems",
      sub: "Same craft, genuinely different physics — and different defect signatures.",
    },
    {
      kind: "paragraph",
      text:
        "Grey iron is the forgiving workhorse: high carbon, excellent castability, flake graphite, usually poured into green sand. Its defects cluster around gas porosity, shrinkage and sand-related problems. Ductile iron looks similar but is metallurgically fussier — magnesium treatment turns the graphite spherical, so nodularity, fade and dross become first-class concerns that simply do not exist in grey iron. Same furnace, different control problem.",
    },
    {
      kind: "paragraph",
      text:
        "Steel raises the temperature and the stakes. Melted in an electric arc furnace and often poured into no-bake chemically-bonded sand, its higher pouring temperatures, greater shrinkage and reactivity push the defect spotlight onto hot tears, shrinkage cavities and slag inclusions — with far less margin for error than iron. The chemistry and thermal regime are a different world.",
    },
    {
      kind: "paragraph",
      text:
        "Aluminium splits into its own sub-families. High-pressure die casting is fast, high-volume and die-bound, with porosity from entrapped gas as the signature worry. Gravity and low-pressure permanent-mould aluminium are slower and gentler, trading cycle time for soundness, with their own balance of shrinkage and oxide inclusions. The alloy is the same element; the process discipline is not.",
    },
    {
      kind: "paragraph",
      text:
        "Then the non-ferrous specialists. Copper-base alloys — bronzes and brasses — bring high thermal conductivity and gas pickup, making feeding and porosity control distinctive. Zinc, often pressure die-cast, runs cool and fast with its own surface defects. Magnesium adds reactivity and handling constraints that dominate everything else. Eight archetypes — and the defects that keep each plant awake differ for every one.",
    },
    {
      kind: "metricRow",
      metrics: [
        {
          value: "~660 °C",
          label: "Aluminium pour, roughly",
          caption: "Light alloy, low pour temperature — a different thermal regime entirely.",
          tone: "info",
        },
        {
          value: "~1450 °C",
          label: "Grey iron pour, roughly",
          caption: "Hot, forgiving melt poured into sand under gravity.",
          tone: "warning",
        },
        {
          value: ">1600 °C",
          label: "Steel pour, roughly",
          caption: "Hotter still, more reactive, far less margin for error.",
          tone: "danger",
        },
      ],
    },
    {
      kind: "callout",
      tone: "info",
      title: "Why the temperatures matter here",
      text:
        "These figures are illustrative, generic ranges — every alloy and shop differs. The point is the spread: a tool whose defaults, alarm bands and defect lists are tuned for one row above will mislead you on the others.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The choice",
      heading: "A configurable twin versus a hard-coded tool",
      sub: "The same difference as a spreadsheet you can edit versus a form someone else locked.",
    },
    {
      kind: "paragraph",
      text:
        "Faced with this variety, software takes one of two philosophies. The point tool bakes a single archetype into its bones: the entities, units, parameter names, defect catalogue and analytics are all assumptions about, say, grey iron in green sand. It can be excellent — for that one case. The moment your plant adds a ductile line, takes on aluminium work, or you try to deploy across a group with mixed processes, you are fighting the tool's assumptions. The configurable twin takes the other route: a general model — entities with typed ports, material classes, operations, lineage — within which you describe your specific process. The grey-iron shop and the aluminium die-caster run the same platform, configured differently.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: effort to support a new archetype",
      unit: "relative effort",
      max: 10,
      bars: [
        { label: "Hard-coded point tool — add a second process", value: 9, tone: "danger", display: "high (rebuild)" },
        { label: "Configurable twin — add a second process", value: 3, tone: "success", display: "low (configure)" },
      ],
      caption:
        "Generic, illustrative comparison. With a general model, a new archetype is a configuration exercise; with a hard-coded tool it tends to mean waiting for the vendor to build it.",
    },
    {
      kind: "paragraph",
      text:
        "The objection to generality is that it sounds like a compromise — jack of all trades, master of none. That worry is fair only if the general model is shallow, and a good one is not. The trick is to be industry-agnostic in the foundations but domain-deep in the content: the entity-and-lot model is general, while ready-made templates, material classes and defect catalogues carry the specific knowledge of grey iron, ductile or aluminium. You get a specialist's depth for your archetype and the freedom to describe a different one tomorrow without changing platforms.",
    },
    {
      kind: "pullQuote",
      quote:
        "The question is not whether a tool fits your process today. It is whether it still fits when your process changes — because it will.",
      attribution: "A foundry group's procurement lesson",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Why it compounds",
      heading: "Generality pays off most across a group",
    },
    {
      kind: "paragraph",
      text:
        "For a single-process shop the difference is real but bounded. For anyone running multiple plants or processes, it compounds. With a configurable platform every site speaks the same underlying model, so a metallurgist moving between a grey-iron plant and an aluminium plant reads the same kind of process graph, lineage and correlation views — only the content differs. Training, reporting and best-practice all travel. With a patchwork of point tools, each site is an island and the group never builds a shared picture. Generality is not academic neatness; it is what lets knowledge move between plants instead of being trapped in each one.",
    },
    {
      kind: "paragraph",
      text:
        "So the eight archetypes are really an argument disguised as a list. Casting is not one process; it is a family of genuinely different ones that share a name. A tool that pretends otherwise serves one member well and quietly misleads you on the rest. A platform that models the family — general where the structure is shared, specific where the knowledge lives — fits the one you run now and the one you might run next. In an industry where the next contract can change which metal you pour, that flexibility is not a luxury; it is the whole point.",
    },
    {
      kind: "sahasEnables",
      intro:
        "The case for generality is industry-wide, not ours to own. What we built is one platform that lives it: a general entity-and-lot model underneath, with foundry-deep templates and catalogues on top — configured, not rebuilt, per archetype.",
      capabilities: [
        {
          title: "One model, many archetypes",
          detail:
            "Typed ports, material classes, operations and lineage are general by design — grey iron, ductile, steel or aluminium are configurations of the same platform.",
          icon: "boxes",
        },
        {
          title: "Domain depth, ready-made",
          detail:
            "Process templates, material classes and defect catalogues carry the specific knowledge of each archetype, so you start deep instead of from a blank canvas.",
          icon: "factory",
        },
        {
          title: "Knowledge travels across sites",
          detail:
            "Every plant speaks the same underlying model, so dashboards, reports and root-cause views move between processes and sites instead of being trapped per tool.",
          icon: "workflow",
        },
      ],
    },
  ],
};
